import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/I18nContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { User, Mail, Phone, MapPin, Store, Briefcase, Wallet, ShieldCheck, LogOut, Loader2 } from "lucide-react";

const Row = ({ Icon, label, value }) => (
  <div className="flex items-start gap-3 py-3 border-b border-border last:border-0" data-testid={`profile-row-${label}`}>
    <span className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0"><Icon className="h-4 w-4 text-primary" /></span>
    <div className="min-w-0">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-sm font-semibold text-secondary break-words">{value || "—"}</p>
    </div>
  </div>
);

export default function Profile() {
  const { user, logout } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();

  if (user === undefined) {
    return <div className="container-page py-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  }
  if (!user) { navigate("/connexion"); return null; }

  const isSeller = user.role === "seller";
  const location = [user.neighborhood, user.city, user.region].filter(Boolean).join(", ");
  const specText = Array.isArray(user.specialty)
    ? user.specialty.map((s) => t(`categories.${s}`)).join(", ")
    : (user.specialty || "");
  const pm = user.payout_methods || {};
  const payout = [
    pm.orange_money?.enabled && `Orange Money${pm.orange_money.number ? ` (${pm.orange_money.number})` : ""}`,
    pm.wave_money?.enabled && `Wave Money${pm.wave_money.number ? ` (${pm.wave_money.number})` : ""}`,
    pm.moov_money?.enabled && `Moov Money${pm.moov_money.number ? ` (${pm.moov_money.number})` : ""}`,
    pm.bank?.enabled && `Virement${pm.bank.rib ? ` (${pm.bank.rib})` : ""}`,
  ].filter(Boolean).join(" · ");

  return (
    <div className="container-page py-10 max-w-2xl">
      <div className="flex items-center gap-4 mb-8">
        <span className="h-16 w-16 rounded-2xl bg-secondary/10 flex items-center justify-center"><User className="h-7 w-7 text-secondary" /></span>
        <div>
          <h1 className="font-heading font-extrabold text-2xl text-secondary" data-testid="profile-name">{user.name}</h1>
          <p className="text-sm text-muted-foreground flex items-center gap-1.5">
            {isSeller ? <Store className="h-3.5 w-3.5" /> : <User className="h-3.5 w-3.5" />}
            {isSeller ? t("profile.sellerAccount") : t("profile.buyerAccount")}
            {user.verified && <span className="inline-flex items-center gap-1 text-success ml-1"><ShieldCheck className="h-3.5 w-3.5" /> {t("profile.verified")}</span>}
          </p>
        </div>
      </div>

      <div className="rounded-2xl bg-card border border-border p-5" data-testid="profile-card">
        {isSeller && <Row Icon={Store} label={t("profile.farmName")} value={user.name} />}
        <Row Icon={Mail} label={t("profile.email")} value={user.email} />
        <Row Icon={Phone} label={t("profile.phone")} value={user.phone} />
        <Row Icon={MapPin} label={t("profile.location")} value={location} />
        {isSeller && (specText || user.farm_type) && (
          <Row Icon={Store} label={t("profile.activity")} value={[specText, user.farm_type, user.farm_size].filter(Boolean).join(" · ")} />
        )}
        {!isSeller && (user.activity_type || user.profession) && (
          <Row Icon={Briefcase} label={t("profile.activity")} value={[user.activity_type, user.profession].filter(Boolean).join(" · ")} />
        )}
        {isSeller && payout && <Row Icon={Wallet} label={t("profile.payout")} value={payout} />}
      </div>

      <div className="flex flex-wrap gap-3 mt-6">
        <Button onClick={() => navigate(isSeller ? "/vendeur" : "/acheteur")} className="rounded-full bg-primary hover:bg-primary/90" data-testid="profile-dashboard-btn">
          {t("nav.dashboard")}
        </Button>
        <Button variant="outline" onClick={() => { logout(); navigate("/"); }} className="rounded-full text-destructive border-destructive/40" data-testid="profile-logout-btn">
          <LogOut className="h-4 w-4 mr-2" /> {t("nav.logout")}
        </Button>
      </div>
    </div>
  );
}
